import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import React, { forwardRef, useCallback } from 'react';
import { Controller, FieldError, Path, useFieldArray, useFormContext } from 'react-hook-form';
import {
  OneBrandPromoUiModuleBody,
  OneBrandPromoUiModuleBodyPaymentMethodOptionBody,
  OneBrandPromoUiModuleBodyPlanCardPartnerLogoBody
} from '../../../../../API';
import { useLocales, useTheme } from '../../../../../hooks';
import { LocaleKeys } from '../../../../../locales/i18n';
import { AssetTypes, AssetTypesEnum } from '../../../../../utils/assetTypes';
import { stringToNumber } from '../../../../../utils/consts/uiModules';
import { markAsRequired } from '../../../../../utils/formHelpers';
import AssetBrowser from '../../../../Assets/AssetBrowser';
import { LokaliseAutocomplete } from '../../../../shared/LokaliseAutocomplete';
import TextValidator from '../../../../shared/TextValidator';
import { FormBody, FormField } from '../../styles';
import { UIModuleTypeForm } from '../../UIModuleForm';
import Button from '../../../../shared/Button';
import { Add, Delete } from '@mui/icons-material';
import generateId from '../../../../../utils/generateId';
import { Sortable } from '../../../../shared/Sortable';
import IconButton from '../../../../shared/IconButton';
import FoldableSection from '../../../../shared/FoldableSection';
import { withStyles } from 'tss-react/mui';
import { withLayoutErrors } from '../../../../../state/Layouts';
import { useRecoilValue } from 'recoil';
import { intersection } from 'lodash';
import { LokaliseField } from '../../../../shared/LokaliseFields';
import { oneBrandPromoFormTestIds } from '../../../../shared/TestsIds';

const CustomFoldableSection = withStyles(FoldableSection, (theme) => ({
  root: {
    ['&.MuiAccordion-root']: {
      backgroundColor: theme.palette.background.default
    }
  },
  region: {
    ['& .MuiAccordionDetails-root']: {
      padding: theme.spacing(2)
    }
  }
}));

type OneBrandPromoFieldProps = {
  name: Path<OneBrandPromoUiModuleBody>;
  required?: boolean;
  'data-testid'?: string;
};

interface OneBrandPromoPaymentMethodOptionsFieldProps extends Omit<OneBrandPromoFieldProps, 'name'> {
  name: keyof OneBrandPromoUiModuleBodyPaymentMethodOptionBody;
  assetType: AssetTypesEnum;
  position: number;
}

interface OneBrandPromoPartnerLogoFieldProps extends Omit<OneBrandPromoFieldProps, 'name'> {
  name: keyof OneBrandPromoUiModuleBodyPlanCardPartnerLogoBody;
  assetType: AssetTypesEnum;
  position: number;
}

const OneBrandPromoForm = forwardRef<UIModuleTypeForm<OneBrandPromoUiModuleBody>>(() => {
  const { control, watch } = useFormContext<OneBrandPromoUiModuleBody>();
  const { t } = useLocales();
  const { formControlColor, theme } = useTheme();
  const layoutErrors = useRecoilValue(withLayoutErrors);
  const {
    append: appendPaymentMethod,
    remove: removePaymentMethod,
    fields: fieldsPaymentMethod,
    replace: replacePaymentMethod
  } = useFieldArray({ control, name: 'paymentMethodOptions' });
  const {
    append: appendPartnerLogo,
    remove: removePartnerLogo,
    fields: fieldsPartnerLogo,
    replace: replacePartnerLogo
  } = useFieldArray({ control, name: 'planCardPartnerLogos' });

  const moduleId = watch('moduleId');

  const renderAssetPicker = useCallback(
    (
      name:
        | Path<OneBrandPromoUiModuleBody>
        | keyof OneBrandPromoUiModuleBodyPaymentMethodOptionBody
        | keyof OneBrandPromoUiModuleBodyPlanCardPartnerLogoBody,
      assetType: AssetTypesEnum,
      moduleId: string,
      required: boolean,
      value: string,
      onChange: (event: string | React.ChangeEvent<Element>) => void,
      error: FieldError | undefined
    ) => (
      <FormField>
        <AssetBrowser
          label={markAsRequired(t(`layouts.one_brand_promo.fields.${name}`), required)}
          assetId={moduleId}
          assetType={assetType}
          value={value}
          data-testid={oneBrandPromoFormTestIds.formField(name)}
          hasError={!!error}
          onChange={onChange}
        />
      </FormField>
    ),
    [t]
  );

  const AssetPicker = useCallback(
    ({
      name,
      assetType,
      required = false
    }: OneBrandPromoFieldProps & { assetType: AssetTypesEnum }): React.ReactElement => (
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) =>
          renderAssetPicker(name, assetType, moduleId || '', required, value as string, onChange, error)
        }
      />
    ),
    [control, moduleId, renderAssetPicker]
  );

  const AssetPickerPaymentMethodOptions = useCallback(
    ({
      name,
      assetType,
      position,
      required = false
    }: OneBrandPromoPaymentMethodOptionsFieldProps): React.ReactElement => (
      <Controller
        control={control}
        name={`paymentMethodOptions.${position}.${name}`}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) =>
          renderAssetPicker(name, assetType, moduleId || '', required, value as string, onChange, error)
        }
      />
    ),
    [control, moduleId, renderAssetPicker]
  );

  const AssetPickerPartnerLogo = useCallback(
    ({ name, assetType, position, required = false }: OneBrandPromoPartnerLogoFieldProps): React.ReactElement => (
      <Controller
        control={control}
        name={`planCardPartnerLogos.${position}.${name}`}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) =>
          renderAssetPicker(name, assetType, moduleId || '', required, value as string, onChange, error)
        }
      />
    ),
    [control, moduleId, renderAssetPicker]
  );

  const LokaliseFieldPaymentMethodOptions = (
    name: keyof OneBrandPromoUiModuleBodyPaymentMethodOptionBody,
    position: number,
    required = false
  ): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={`paymentMethodOptions.${position}.${name}`}
        render={({ field: { value, onChange } }) => (
          <LokaliseAutocomplete
            label={t(`layouts.one_brand_promo.fields.${name}` as LocaleKeys)}
            name={name}
            value={value as string}
            onChange={onChange}
            required={required}
            data-testid={oneBrandPromoFormTestIds.formField(name)}
          />
        )}
      />
    </FormField>
  );

  const CheckboxField = ({ name }: OneBrandPromoFieldProps): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            label={t(`layouts.one_brand_promo.fields.${name}` as LocaleKeys)}
            control={
              <Checkbox
                name={t('layouts.use_collection_name')}
                checked={value as boolean}
                color={formControlColor}
                onChange={onChange}
                data-testid={oneBrandPromoFormTestIds.formField(name)}
              />
            }
          />
        )}
      />
    </FormField>
  );

  const NumberField = ({ name }: OneBrandPromoFieldProps): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange } }) => (
          <TextValidator
            type="number"
            name={name}
            value={value}
            label={t(`layouts.one_brand_promo.fields.${name}` as LocaleKeys)}
            validators={['minNumber:0', 'matchRegexp:^[0-9]+$']}
            color={formControlColor}
            fullWidth
            onChange={({ target }) => onChange(stringToNumber((target as HTMLInputElement).value))}
            data-testid={oneBrandPromoFormTestIds.formField(name)}
          />
        )}
      />
    </FormField>
  );

  const TextField = ({ name }: OneBrandPromoFieldProps): React.ReactElement => (
    <FormField>
      <Controller
        control={control}
        name={name}
        render={({ field: { value } }) => (
          <TextValidator
            type="text"
            disabled={true}
            name={name}
            value={value}
            label={t(`layouts.one_brand_promo.fields.${name}` as LocaleKeys)}
            color={formControlColor}
            fullWidth
            data-testid={oneBrandPromoFormTestIds.formField(name)}
          />
        )}
      />
    </FormField>
  );

  const headerColor = (required: string[]) =>
    intersection(layoutErrors, required).length
      ? {
          color: theme.palette.error.main
        }
      : {};

  const onAddPaymentMethodOptions = () => {
    appendPaymentMethod({
      id: generateId(),
      paymentMethodOptionIcon: '',
      paymentMethodOptionText: ''
    });
  };

  const onAddPartnerLogos = () => {
    appendPartnerLogo({
      id: generateId(),
      planCardPartnerLogo: ''
    });
  };

  return (
    <FormBody data-testid={oneBrandPromoFormTestIds.formBody}>
      <CustomFoldableSection
        summary={
          <Typography sx={headerColor(['landscapeFillImage', 'portraitFillImage'])} variant="h6">
            {t('layouts.one_brand_promo.headers.images')}
          </Typography>
        }
        expanded
        disableGutters
      >
        <AssetPicker name="landscapeFillImage" assetType={AssetTypes.oneBrandPromo.landscape} required />
        <AssetPicker name="portraitFillImage" assetType={AssetTypes.oneBrandPromo.portrait} required />
        <AssetPicker name="desktopFillImage" assetType={AssetTypes.oneBrandPromo.desktop} />
        <AssetPicker name="mobileFillImage" assetType={AssetTypes.oneBrandPromo.mobile} />
        <AssetPicker name="ctvFillImage" assetType={AssetTypes.oneBrandPromo.ctv} />
        <AssetPicker name="tabletFillImage" assetType={AssetTypes.oneBrandPromo.tablet} />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={
          <Typography sx={headerColor(['popupHeader', 'popupSubheader', 'popupCtaText'])} variant="h6">
            {t('layouts.one_brand_promo.headers.popup_paywall')}
          </Typography>
        }
        expanded
        disableGutters
      >
        <LokaliseField
          name="popupHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupHeader')}
          required
        />
        <LokaliseField
          name="popupSubheader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupSubheader')}
          required
        />
        <LokaliseField
          name="popupText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupText')}
        />
        <LokaliseField
          name="popupCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupCtaText')}
          required
        />
        <LokaliseField
          name="popupAnonSignInCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupAnonSignInCtaText')}
        />
        <LokaliseField
          name="popupAuthSignInCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupAuthSignInCtaText')}
        />
        <LokaliseField
          name="popupValuePropositions"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupValuePropositions')}
        />
        <LokaliseField
          name="popupLegalDisclosure"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupLegalDisclosure')}
        />
        <LokaliseField
          name="popupGiftCardCta"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('popupGiftCardCta')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={
          <Typography sx={headerColor(['fullHeader', 'fullSubheader', 'fullCtaText'])} variant="h6">
            {t('layouts.one_brand_promo.headers.full_paywall')}
          </Typography>
        }
        expanded
        disableGutters
      >
        <LokaliseField
          name="fullHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullHeader')}
          required
        />
        <LokaliseField
          name="fullSubheader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullSubheader')}
          required
        />
        <LokaliseField
          name="fullValuePropositions"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullValuePropositions')}
        />
        <LokaliseField
          name="fullCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullCtaText')}
          required
        />
        <LokaliseField
          name="fullText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullText')}
        />
        <LokaliseField
          name="fullAnonSignInCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullAnonSignInCtaText')}
        />
        <LokaliseField
          name="fullAuthSignInCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullAuthSignInCtaText')}
        />
        <LokaliseField
          name="fullLegalDisclosure"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullLegalDisclosure')}
        />
        <LokaliseField
          name="fullGiftCardCta"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('fullGiftCardCta')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={<Typography variant="h6">{t('layouts.one_brand_promo.headers.plan_picker')}</Typography>}
        disableGutters
      >
        <LokaliseField
          name="planPickerHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerHeader')}
        />
        <LokaliseField
          name="planPickerSubheader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerSubheader')}
        />
        <LokaliseField
          name="planPickerPlanName"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPlanName')}
        />
        <LokaliseField
          name="planPickerPlanDescription"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPlanDescription')}
        />
        <LokaliseField
          name="planPickerPlanPrice"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPlanPrice')}
        />
        <LokaliseField
          name="planPickerPlanSavings"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPlanSavings')}
        />
        <CheckboxField name="isPlanPickerPlanHighlighted" />
        <CheckboxField name="isPlanPickerPlanDefault" />
        <LokaliseField
          name="planPickerValuePropositionHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerValuePropositionHeader')}
        />
        <NumberField name="planPickerPlanPriority" />
        <LokaliseField
          name="planPickerValuePropositions"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerValuePropositions')}
        />
        <LokaliseField
          name="planPickerFreeAccountCtaText"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerFreeAccountCtaText')}
        />
        <LokaliseField
          name="planPickerLegalDisclosure"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerLegalDisclosure')}
        />
        <LokaliseField
          name="planPickerInstructions"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerInstructions')}
        />
        <LokaliseField
          name="planPickerPartnerDisclaimer"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPartnerDisclaimer')}
        />
        <LokaliseField
          name="ctaAcceptChange"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('ctaAcceptChange')}
        />
        <LokaliseField
          name="headerFreeCard"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('headerFreeCard')}
        />
        <LokaliseField
          name="subheaderFreeCard"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('subheaderFreeCard')}
        />
        <LokaliseField
          name="listOfLostBenefits"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('listOfLostBenefits')}
        />
        <LokaliseField
          name="ctaCancelPremium"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('ctaCancelPremium')}
        />
        <LokaliseField
          name="ctaKeepSubscription"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('ctaKeepSubscription')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={<Typography variant="h6">{t('layouts.one_brand_promo.headers.plan_switching')}</Typography>}
        disableGutters
      >
        <LokaliseField
          name="planSwitchingHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planSwitchingHeader')}
        />
        <LokaliseField
          name="planSwitchingSubheader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planSwitchingSubheader')}
        />
        <LokaliseField
          name="planPickerPriceTag"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerPriceTag')}
        />
        <LokaliseField
          name="planPickerCurrentPriceTag"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planPickerCurrentPriceTag')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={<Typography variant="h6">{t('payments.payment_method')}</Typography>}
        disableGutters
      >
        <LokaliseField
          name="paymentMethodHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('paymentMethodHeader')}
        />
        <div>
          <Sortable
            list={fieldsPaymentMethod}
            setList={replacePaymentMethod}
            ghostClass="sortableGhost"
            dragClass="sortableDragDefault"
          >
            {fieldsPaymentMethod.map((paymentMethod, i) => (
              <Stack
                key={paymentMethod.id}
                data-testid={oneBrandPromoFormTestIds.paymentMethodOptions}
                gap={6}
                marginBottom={5}
                direction="row"
                alignItems="center"
              >
                <AssetPickerPaymentMethodOptions
                  name="paymentMethodOptionIcon"
                  assetType={AssetTypes.oneBrandPromo.ctv}
                  position={i}
                />
                {LokaliseFieldPaymentMethodOptions('paymentMethodOptionText', i)}
                <IconButton
                  title={t('layouts.one_brand_promo.actions.remove_payment_method')}
                  disabled={fieldsPaymentMethod.length === 1}
                  onClick={() => removePaymentMethod(i)}
                  sx={{ mt: 4 }}
                  data-testid={oneBrandPromoFormTestIds.paymentMethodOptionsRemoveButton}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
          </Sortable>
          <Button
            startIcon={<Add />}
            color={formControlColor}
            variant="outlined"
            onClick={onAddPaymentMethodOptions}
            data-testid={oneBrandPromoFormTestIds.paymentMethodOptionsAddButton}
          >
            {t('layouts.one_brand_promo.actions.add_payment_method')}
          </Button>
        </div>
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={<Typography variant="h6">{t('payments.plan_selection')}</Typography>}
        disableGutters
      >
        <CheckboxField name="planSelectionToggle" />
        <div />
        <LokaliseField
          name="planSelectionHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planSelectionHeader')}
        />
        <LokaliseField
          name="planSelectionToggleOpt1"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planSelectionToggleOpt1')}
        />
        <LokaliseField
          name="planSelectionToggleOpt2"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planSelectionToggleOpt2')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection summary={<Typography variant="h6">{t('payments.plan_card')}</Typography>} disableGutters>
        <CheckboxField name="isPlanCardBundlePlan" />
        <CheckboxField name="isAMonthlyPlan" />
        <div />
        <LokaliseField
          name="planCardChoosePlanCta"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardChoosePlanCta')}
        />
        <LokaliseField
          name="planCardMoreBenefitsCta"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardMoreBenefitsCta')}
        />
        <LokaliseField
          name="planCardTag"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardTag')}
        />
        <LokaliseField
          name="collapseMoreBenefitsCta"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('collapseMoreBenefitsCta')}
        />
        <LokaliseField
          name="planCardLegalDisclosure"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardLegalDisclosure')}
        />
        <LokaliseField
          name="planCardLegalDisclosureDetails"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardLegalDisclosureDetails')}
        />
        <LokaliseField
          name="planCardLegalDisclosureBody"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('planCardLegalDisclosureBody')}
        />
        <TextField name="planCardTier" />
        <AssetPicker name="planCardVixLogo" assetType={AssetTypes.accountCreation.vixLogo} />
        <Sortable
          list={fieldsPartnerLogo}
          setList={replacePartnerLogo}
          ghostClass="sortableGhost"
          dragClass="sortableDragDefault"
        >
          {fieldsPartnerLogo.map((partnerLogo, i) => (
            <Stack
              key={partnerLogo.id}
              data-testid={oneBrandPromoFormTestIds.planCardPartnerLogos}
              gap={6}
              marginBottom={5}
              direction="row"
              alignItems="center"
            >
              <AssetPickerPartnerLogo
                name="planCardPartnerLogo"
                assetType={AssetTypes.oneBrandPromo.ctv}
                position={i}
              />
              <IconButton
                title={t('layouts.one_brand_promo.actions.remove_partner_logo')}
                disabled={fieldsPartnerLogo.length === 1}
                onClick={() => removePartnerLogo(i)}
                sx={{ mt: 4 }}
                data-testid={oneBrandPromoFormTestIds.planCardPartnerLogosRemoveButton}
              >
                <Delete />
              </IconButton>
            </Stack>
          ))}
        </Sortable>
        <Button
          startIcon={<Add />}
          color={formControlColor}
          variant="outlined"
          onClick={onAddPartnerLogos}
          data-testid={oneBrandPromoFormTestIds.planCardPartnerLogosAddButton}
        >
          {t('layouts.one_brand_promo.actions.add_partner_logo')}
        </Button>
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={<Typography variant="h6">{t('layouts.one_brand_promo.headers.spin_premia')}</Typography>}
        disableGutters
      >
        <LokaliseField
          name="spinPremia.points"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('spinPremia.points')}
        />
        <LokaliseField
          name="spinPremia.description"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('spinPremia.description')}
        />
        <CheckboxField name="spinPremia.checkbox" />
        <LokaliseField
          name="spinPremia.checkboxCopy"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('spinPremia.checkboxCopy')}
        />
      </CustomFoldableSection>
      <CustomFoldableSection
        summary={
          <Typography variant="h6">{t('layouts.one_brand_promo.headers.gift_card_balance_reminder')}</Typography>
        }
        disableGutters
      >
        <AssetPicker name="giftCardLandscapeFillImage" assetType={AssetTypes.oneBrandPromo.landscape} />
        <AssetPicker name="giftCardPortraitFillImage" assetType={AssetTypes.oneBrandPromo.portrait} />
        <AssetPicker name="giftCardDesktopFillImage" assetType={AssetTypes.oneBrandPromo.desktop} />
        <AssetPicker name="giftCardMobileFillImage" assetType={AssetTypes.oneBrandPromo.mobile} />
        <AssetPicker name="giftCardCtvFillImage" assetType={AssetTypes.oneBrandPromo.ctv} />
        <AssetPicker name="giftCardTabletFillImage" assetType={AssetTypes.oneBrandPromo.tablet} />
        <div />
        <LokaliseField
          name="giftCardBalanceReminderHeader"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderHeader')}
        />
        <LokaliseField
          name="giftCardBalanceReminderSubtitle"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderSubtitle')}
        />
        <LokaliseField
          name="giftCardBalanceReminderOne"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderOne')}
        />
        <LokaliseField
          name="giftCardBalanceReminderTwo"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderTwo')}
        />
        <LokaliseField
          name="giftCardBalanceReminderCtaOne"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderCtaOne')}
        />
        <LokaliseField
          name="giftCardBalanceReminderCtaTwo"
          control={control}
          toTranslate="layouts.one_brand_promo.fields"
          dataTestId={oneBrandPromoFormTestIds.formField('giftCardBalanceReminderCtaTwo')}
        />
      </CustomFoldableSection>
    </FormBody>
  );
});

OneBrandPromoForm.displayName = 'OneBrandPromo';

export default OneBrandPromoForm;
